import { useCallback, useEffect, useState } from "react";
import { loadCatalog } from "./i18n";
import { detectLocale } from "./locale-utils";

export const DISPLAY_LANGUAGE_STORAGE_KEY = "display_language";
export const DEFAULT_DISPLAY_LANGUAGE = "de";
export const DISPLAY_LANGUAGE_CHANGE_EVENT = "display-language-change";

// Fallback for the *call's* source language when room metadata lacks
// `language_config.language_code`. Distinct from DEFAULT_DISPLAY_LANGUAGE
// (the admin's UI + translation target) — the two just happen to coincide.
export const DEFAULT_SOURCE_LANGUAGE = "de";

let initialized = false;

export function getDisplayLanguage(): string {
  return (
    localStorage.getItem(DISPLAY_LANGUAGE_STORAGE_KEY) ??
    DEFAULT_DISPLAY_LANGUAGE
  );
}

/**
 * Persist the chosen language and drive the derived UI state: activate the
 * matching Lingui catalog, set `<html lang>`, and broadcast the change so
 * every `useDisplayLanguage` consumer re-renders.
 */
function applyDisplayLanguage(lang: string): void {
  localStorage.setItem(DISPLAY_LANGUAGE_STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  void loadCatalog(lang);
  globalThis.dispatchEvent(
    new CustomEvent(DISPLAY_LANGUAGE_CHANGE_EVENT, { detail: lang }),
  );
}

/**
 * Bootstrap the interface + transcript language at startup. Honors a stored
 * choice; otherwise detects the best UI catalog from the browser's preferred
 * languages. Idempotent, mirrors `initTheme()`; call once at module load.
 */
export function initDisplayLanguage(): void {
  if (initialized) return;
  initialized = true;

  const stored = localStorage.getItem(DISPLAY_LANGUAGE_STORAGE_KEY);
  applyDisplayLanguage(stored ?? detectLocale(navigator.languages));
}

export function useDisplayLanguage(): [string, (lang: string) => void] {
  const [language, setLanguage] = useState<string>(getDisplayLanguage);

  useEffect(() => {
    const handler = (e: Event) => {
      setLanguage((e as CustomEvent<string>).detail);
    };
    globalThis.addEventListener(DISPLAY_LANGUAGE_CHANGE_EVENT, handler);
    return () =>
      globalThis.removeEventListener(DISPLAY_LANGUAGE_CHANGE_EVENT, handler);
  }, []);

  const persistLanguage = useCallback((lang: string) => {
    applyDisplayLanguage(lang);
    setLanguage(lang);
  }, []);

  return [language, persistLanguage];
}
